/**
 * Shared mapping functions for converting between database and UI
 * representations of request status and priority.
 *
 * These mappers bridge the gap between the database schema
 * (e.g., "create", "interview", "critical") and the UI component
 * props (e.g., "draft", "pending", "urgent").
 */

import type {
  RequestPriority as DbRequestPriority,
  RequestStatus as DbRequestStatus,
} from '@cortex/database';
import type {
  RequestPriority as UiRequestPriority,
  RequestStatus as UiRequestStatus,
} from '@cortex/ui';

/**
 * Maps database request status to UI request status.
 *
 * Database statuses: create, interview, develop, review, finalize, closed
 * UI statuses: draft, pending, in-progress, review, completed, cancelled
 */
export function mapDbStatusToUiStatus(
  dbStatus: DbRequestStatus
): UiRequestStatus {
  const statusMap: Record<DbRequestStatus, UiRequestStatus> = {
    create: 'draft',
    interview: 'pending',
    develop: 'in-progress',
    review: 'review',
    finalize: 'completed',
    closed: 'completed',
  };
  return statusMap[dbStatus];
}

/**
 * Maps database request priority to UI request priority.
 *
 * Database priorities: low, medium, high, critical
 * UI priorities: low, medium, high, urgent
 */
export function mapDbPriorityToUiPriority(
  dbPriority: DbRequestPriority
): UiRequestPriority {
  const priorityMap: Record<DbRequestPriority, UiRequestPriority> = {
    low: 'low',
    medium: 'medium',
    high: 'high',
    critical: 'urgent',
  };
  return priorityMap[dbPriority];
}
